import { motion } from 'motion/react'
import type { CustomerOrder } from '../../customerOrdersApi'
import type { ShopLocale, ShopMessageKey } from '../../../i18n'
import { customerMotionTransition } from '../customer-area'
import { formatCurrency } from '../../helpers/formatters'
import type { CustomerOrderDetailLabels } from './orderDetailShared'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type CustomerOrderProductsSectionProps = {
  orderDetail: CustomerOrder
  locale: ShopLocale
  labels: CustomerOrderDetailLabels
  reducedMotion: boolean
  t: TranslateFn
}

export const CustomerOrderProductsSection = ({
  orderDetail,
  locale,
  labels,
  reducedMotion,
  t,
}: CustomerOrderProductsSectionProps) => (
  <section className="ca-card-shell ca-theme-transition rounded-xl px-5 py-5">
    <header className="flex flex-wrap items-center justify-between gap-2 border-b border-(--color-border-default) pb-2.5">
      <h3 className="text-sm font-semibold text-(--color-text-primary)">{labels.productsTitle}</h3>
      <p className="text-xs font-medium text-(--color-text-secondary)">
        {orderDetail.itemCount} {t('customer.orders.items').toLowerCase()}
      </p>
    </header>

    {orderDetail.items.length === 0 ? (
      <p className="mt-3 rounded-lg border border-(--color-border-default) bg-(--color-bg-surface-muted) px-3 py-2 text-sm text-(--color-text-secondary)">
        {t('customer.orderDetail.itemsEmpty')}
      </p>
    ) : (
      <ul className="mt-3 space-y-3">
        {orderDetail.items.map((item, index) => (
          <motion.li
            key={item.itemId}
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
            transition={{ ...customerMotionTransition, delay: reducedMotion ? 0 : index * 0.02 }}
            className="rounded-xl border border-(--color-border-default) bg-[color-mix(in_srgb,var(--color-bg-card)_96%,var(--color-bg-surface-muted))] px-3.5 py-3 shadow-(--shadow-sm)"
          >
            <article className="grid gap-3 sm:grid-cols-[64px_minmax(0,1fr)_auto] sm:items-center">
              <div className="relative inline-flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-(--color-border-default) bg-[color-mix(in_srgb,var(--color-bg-surface-muted)_78%,var(--color-bg-card))]">
                <span className="text-lg font-semibold text-(--color-text-secondary)">
                  {(item.productName.trim()[0] || '#').toUpperCase()}
                </span>
                <span className="absolute right-1.5 bottom-1.5 h-1.5 w-1.5 rounded-full bg-(--color-action-primary)" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-(--color-text-primary)">{item.productName}</p>
                <p className="mt-1 text-xs text-(--color-text-muted)">
                  {labels.qty}: {item.quantity}
                </p>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-xs text-(--color-text-muted)">
                  {item.quantity} x {formatCurrency(item.unitPriceSnapshot, locale)}
                </p>
                <p className="mt-1 text-sm font-semibold text-(--color-text-primary)">{formatCurrency(item.lineTotal, locale)}</p>
              </div>
            </article>
          </motion.li>
        ))}
      </ul>
    )}
  </section>
)
